import { Select as BaseSelect } from '@base-ui/react/select';
import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';
import { selectPopupMotion } from '../lib/motion';

export const SelectRoot = BaseSelect.Root;
export const SelectValue = BaseSelect.Value;
export const SelectGroup = BaseSelect.Group;

export function SelectTrigger({ className, ...props }: ComponentProps<typeof BaseSelect.Trigger>) {
  return (
    <BaseSelect.Trigger
      className={cn(
        'border-line bg-surface gap-snug px-row flex h-10 w-full items-center justify-between rounded-lg border',
        'text-ink ease-flow duration-fast text-sm transition-colors',
        'hover:border-line-strong data-[popup-open]:border-line-accent',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...props}
    />
  );
}

export function SelectIcon({ className, ...props }: ComponentProps<typeof BaseSelect.Icon>) {
  return (
    <BaseSelect.Icon className={cn('text-ink-faint', className)} {...props}>
      <svg viewBox="0 0 12 12" className="size-3" fill="none" aria-hidden="true">
        <path
          d="M2.5 4.5 6 8l3.5-3.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </BaseSelect.Icon>
  );
}

export function SelectPopup({ className, ...props }: ComponentProps<typeof BaseSelect.Popup>) {
  return (
    <BaseSelect.Popup
      className={cn(
        'max-h-[min(24rem,var(--available-height))] min-w-[var(--anchor-width)] overflow-y-auto',
        'border-line bg-surface-raised shadow-pop rounded-xl border py-1',
        selectPopupMotion,
        className,
      )}
      {...props}
    />
  );
}

export function SelectItem({
  className,
  children,
  ...props
}: ComponentProps<typeof BaseSelect.Item>) {
  return (
    <BaseSelect.Item
      className={cn(
        'text-ink gap-snug px-row py-snug flex cursor-default items-center justify-between text-sm',
        'data-[highlighted]:bg-highlight data-[selected]:text-action',
        className,
      )}
      {...props}
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
      <BaseSelect.ItemIndicator className="text-action">
        <svg viewBox="0 0 12 12" className="size-3" fill="none" aria-hidden="true">
          <path
            d="M1.5 6.2 4.4 9l6-6.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  );
}

export interface SelectContentProps extends ComponentProps<typeof BaseSelect.Popup> {
  sideOffset?: number;
}

export function SelectContent({ sideOffset = 4, children, ...props }: SelectContentProps) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        sideOffset={sideOffset}
        alignItemWithTrigger={false}
        className="z-popover"
      >
        <SelectPopup {...props}>{children}</SelectPopup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

export const Select = {
  Root: SelectRoot,
  Trigger: SelectTrigger,
  Value: SelectValue,
  Icon: SelectIcon,
  Content: SelectContent,
  Item: SelectItem,
  Group: SelectGroup,
};
